import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import wedlLogo from '../../assets/images/wedl_logo.svg';

const SupplierQuestionnaireSuccess: React.FC = () => {
  const { masterToken } = useParams<{ masterToken: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const supplierName = searchParams.get('supplier') || 'Lieferant';
  const questionnaireName = searchParams.get('questionnaire') || 'Fragebogen';
  const completedDate = new Date().toLocaleDateString('de-DE');

  // Auto-redirect nach 10 Sekunden
  useEffect(() => {
    const timer = setTimeout(() => {
      if (masterToken) {
        navigate(`/supplier/portal/${masterToken}`, { replace: true });
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [masterToken, navigate]);

  const handleReturnToDashboard = () => {
    if (masterToken) {
      navigate(`/supplier/portal/${masterToken}`, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        {/* WEDL Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-32 h-32 flex items-center justify-center mx-auto mb-4">
            <img src={wedlLogo} alt="WEDL" className="w-24 h-24 object-contain" />
          </div>
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600 tracking-wider">WEDL</h2>
            <p className="text-sm text-gray-600">Ihr vertrauensvoller Partner</p>
          </div>
        </div>

        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <FontAwesomeIcon 
            icon={faCheckCircle} 
            className="text-5xl text-green-500" 
          />
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Vielen Dank!
          </h1>
          <p className="text-lg text-gray-700 mb-8">
            Ihr Fragebogen wurde erfolgreich abgeschlossen. Wir haben Ihre Antworten erhalten.
          </p>
        </motion.div>

        {/* Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-50 rounded-lg p-6 mb-8 text-left"
        >
          <div className="space-y-3">
            <div>
              <span className="font-medium text-gray-700">Lieferant:</span>
              <span className="ml-2 text-gray-900">{supplierName}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Fragebogen:</span>
              <span className="ml-2 text-gray-900">{questionnaireName}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Abgeschlossen:</span>
              <span className="ml-2 text-gray-900">{completedDate}</span>
            </div>
          </div>
        </motion.div>

        {/* Return Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <button
            onClick={handleReturnToDashboard}
            className="w-full flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Zurück zum Dashboard
          </button>
          
          <p className="text-sm text-gray-500 mt-4">
            Sie werden automatisch in 10 Sekunden weitergeleitet...
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SupplierQuestionnaireSuccess;
